import { useTheme } from "@emotion/react";
import React, { useMemo, useState } from "react";
import { MdAdd, MdEdit } from "react-icons/md";
import { useQueryClient } from "react-query";

import { useClientManager } from "@megaron/react-clients";
import { CustomerDoc, isDocOfType } from "@megaron/docs-contracts";
import { TicketStatus, type TicketCategory, type TicketDetailDto } from "@megaron/support-contracts";
import { Uuid } from "@megaron/uuid";
import { Button } from "@dash/form";
import { DocsSelectDialog } from "@dash/select";

import { CrmCustomerCard } from "../crm/CrmCustomerCard";
import { SupportTicketProductsEditField } from "../products/SupportTicketProductsField";
import { SupportEditTagsDialog } from "../shared/SupportEditTagsDialog";
import { SupportFieldLabel } from "../shared/SupportFieldLabel";

type Props = {
  ticketId: Uuid;
  subject: string;
  status: TicketStatus;
  priority: "low" | "medium" | "high" | "urgent" | null;
  category: TicketCategory | null;
  invoiceNumber: string | null;
  reporterEmail: string;
  customerUuids: Uuid[];
  tags: string[];
  products: TicketDetailDto["products"];
};

function matchCustomerByEmail(items: CustomerDoc[], email: string): CustomerDoc | undefined {
  const lower = email.trim().toLowerCase();
  return items.find((c) => c.email?.trim().toLowerCase() === lower);
}

export const SupportTicketContactCrmCard: React.FC<Props> = ({
  ticketId,
  subject,
  status,
  priority,
  category,
  invoiceNumber,
  reporterEmail,
  customerUuids,
  tags,
  products,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);

  const docsRetrieveQuery = useClientManager("docs")
    .retrieveDocs()
    .useQuery(
      { docIds: customerUuids.map((uuid) => `customer:${uuid}`) },
      { enabled: customerUuids.length > 0 },
    );

  const searchQuery = useClientManager("docs")
    .searchCustomers()
    .useQuery(
      { searchText: reporterEmail.trim(), limit: 10 },
      { enabled: customerUuids.length === 0 && !!reporterEmail.trim() },
    );

  const addMutation = useClientManager("support")
    .addTicketCustomer()
    .useMutation({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] }),
    });

  const removeMutation = useClientManager("support")
    .removeTicketCustomer()
    .useMutation({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] }),
    });

  const linkedCustomers = useMemo((): CustomerDoc[] => {
    if (customerUuids.length === 0) return [];
    return (docsRetrieveQuery.data ?? []).filter((d): d is CustomerDoc => d !== null && isDocOfType("customer")(d));
  }, [customerUuids.length, docsRetrieveQuery.data]);

  const emailMatchedCustomer = useMemo((): CustomerDoc | undefined => {
    if (customerUuids.length > 0) return undefined;
    return matchCustomerByEmail(searchQuery.data?.items ?? [], reporterEmail);
  }, [customerUuids.length, searchQuery.data?.items, reporterEmail]);

  const isLoading =
    (customerUuids.length > 0 ? docsRetrieveQuery.isLoading : false) ||
    (customerUuids.length === 0 ? searchQuery.isLoading : false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });

  const sectionGap = "1rem";

  const hasTags = tags.length > 0;

  const tagsSection = (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
      <SupportFieldLabel>Tagi</SupportFieldLabel>
      <div css={{ display: "flex", gap: "7px", alignItems: "center", color: theme.colors.primary, flexWrap: "wrap" }}>
      {tags.map((tag) => (
        <div
          key={tag}
          css={{
            borderRadius: theme.smallBorderRadius,
            background: theme.colors.primaryLight,
            padding: "5px 10px",
            color: theme.colors.primary,
            cursor: "pointer",
          }}
        >
          {`#${tag}`}
        </div>
      ))}
      <Button
        variant={hasTags ? "text" : "outline"}
        size="small"
        icon={hasTags ? <MdEdit size={18} /> : <MdAdd size={16} />}
        onClick={() => setTagsDialogOpen(true)}
        css={{
          flexShrink: 0,
          ...(hasTags ? { padding: "0.125rem" } : {}),
        }}
      >
        {hasTags ? "Edytuj" : "Dodaj tag"}
      </Button>
      </div>
    </div>
  );

  const tagsDialog = tagsDialogOpen ? (
    <SupportEditTagsDialog
      onClose={() => setTagsDialogOpen(false)}
      ticketId={ticketId}
      subject={subject}
      status={status}
      priority={priority}
      category={category}
      invoiceNumber={invoiceNumber}
      ticketTags={tags}
      onSuccess={invalidate}
    />
  ) : null;

  const productsSection = <SupportTicketProductsEditField ticketId={ticketId} products={products} />;

  const crmSection = (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      {emailMatchedCustomer && <CrmCustomerCard customer={emailMatchedCustomer} />}
      {linkedCustomers.map((customer) => (
        <CrmCustomerCard
          key={customer.uuid}
          customer={customer}
          onDetach={() => removeMutation.mutate({ id: ticketId, customerUuid: customer.uuid })}
        />
      ))}
      {linkedCustomers.length === 0 && !emailMatchedCustomer && (
        <div css={{ padding: "0.75rem 1rem", border: `1px solid ${theme.colors.border}`, borderRadius: theme.smallBorderRadius, fontSize: "14px" }}>
          <span css={{ color: theme.colors.secondaryText }}>Brak w CRM</span>
        </div>
      )}
      <Button icon={<MdAdd />} variant="text" onClick={() => setAddDialogOpen(true)}>
        Przypisz klienta
      </Button>
    </div>
  );

  const loadingCrmSection = (
    <div css={{ padding: "0.75rem 1rem", border: `1px solid ${theme.colors.border}`, borderRadius: theme.smallBorderRadius, fontSize: "14px", color: theme.colors.secondaryText }}>
      Szukam w CRM...
    </div>
  );

  if (isLoading) {
    return (
      <>
        <div css={{ display: "flex", flexDirection: "column" }}>
          {tagsSection}
          <div css={{ marginTop: sectionGap }}>{productsSection}</div>
          <div css={{ marginTop: sectionGap }}>{loadingCrmSection}</div>
        </div>
        {tagsDialog}
      </>
    );
  }

  return (
    <>
      <div css={{ display: "flex", flexDirection: "column" }}>
        {tagsSection}
        <div css={{ marginTop: sectionGap }}>{productsSection}</div>
        <div css={{ marginTop: sectionGap }}>{crmSection}</div>
      </div>
      <DocsSelectDialog
        docType={["customer"]}
        variant="single-select"
        label="Przypisz klienta z CRM"
        noTrigger
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSelect={(option) => {
          if (option && option.docType === "customer" && option.value) {
            setAddDialogOpen(false);
            addMutation.mutate({ id: ticketId, customerUuid: Uuid(option.value) });
          }
        }}
      />
      {tagsDialog}
    </>
  );
};
